import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AEA_PLACE, CONFIG } from './_barrel.js';


        // The subject's location points + inferred places overlaid on a real
        // street basemap. Tiles come from our own cache proxy
        // (/api/v1/aea/tiles/...) so the subject's neighbourhood is never
        // requested from a third-party tile server. If tiles are unavailable the
        // points still render over the dark background — the map never breaks.
        function AeaExposureMap({ points, places }) {
            const ref = useRef(null);

            useEffect(() => {
                if (!ref.current) return undefined;
                const pts = points || [];
                const pls = places || [];

                const map = L.map(ref.current, {
                    preferCanvas: true,
                    zoomControl: true,
                    attributionControl: true,
                    worldCopyJump: true,
                });

                let tilesFailed = 0;
                const layer = L.tileLayer(`${CONFIG.apiBase}/api/v1/aea/tiles/{z}/{x}/{y}.png`, {
                    maxZoom: 19,
                    attribution: '&copy; OpenStreetMap contributors',
                    crossOrigin: true,
                });
                layer.on('tileerror', () => { tilesFailed += 1; });
                layer.addTo(map);

                const renderer = L.canvas({ padding: 0.5 });
                const bounds = [];

                // Reported positions — subsample so very large exports stay smooth.
                const step = pts.length > 2500 ? Math.ceil(pts.length / 2500) : 1;
                pts.forEach((p, i) => {
                    if (i % step) return;
                    L.circleMarker([p.lat, p.lon], {
                        renderer, radius: 2, weight: 0,
                        fillColor: '#c9a96e', fillOpacity: 0.4,
                    }).addTo(map);
                    bounds.push([p.lat, p.lon]);
                });

                // Inferred places — radius ring + a labelled marker with a popup.
                pls.forEach(pl => {
                    const meta = AEA_PLACE[pl.place_type] || AEA_PLACE.transient;
                    L.circle([pl.centroid_lat, pl.centroid_lon], {
                        radius: Math.max(30, pl.radius_m || 60),
                        color: meta.color, weight: 1,
                        fillColor: meta.color, fillOpacity: 0.15,
                    }).addTo(map);
                    const dwellH = Math.round((pl.total_dwell_minutes || 0) / 60);
                    L.marker([pl.centroid_lat, pl.centroid_lon], {
                        icon: L.divIcon({
                            className: 'aea-place-badge',
                            html: `<span style="display:flex;align-items:center;justify-content:center;`
                                + `width:20px;height:20px;border-radius:50%;background:${meta.color};`
                                + `color:#0b0d10;font:700 10px/1 'IBM Plex Mono',monospace;`
                                + `box-shadow:0 0 0 2px rgba(0,0,0,.35)">${meta.label}</span>`,
                            iconSize: [20, 20], iconAnchor: [10, 10],
                        }),
                    }).bindPopup(
                        `<b>${meta.name}</b><br>${pl.visit_count || 0} visit${pl.visit_count === 1 ? '' : 's'} `
                        + `&middot; ${dwellH}h dwell<br>${pl.centroid_lat.toFixed(5)}, ${pl.centroid_lon.toFixed(5)}`
                    ).addTo(map);
                    bounds.push([pl.centroid_lat, pl.centroid_lon]);
                });

                if (bounds.length) {
                    map.fitBounds(L.latLngBounds(bounds).pad(0.25), { maxZoom: 16 });
                } else {
                    map.setView([20, 0], 2);
                }
                // Leaflet needs a resize nudge when it mounts inside a flex/panel.
                setTimeout(() => map.invalidateSize(), 0);

                return () => { map.remove(); };
            }, [points, places]);

            const pts = points || [];
            if (pts.length === 0 && (places || []).length === 0)
                return <div className="px-6 py-10 text-muted text-sm text-center">No positions to plot.</div>;

            return <div ref={ref} role="img" aria-label="Exposure map"
                        style={{ height: 440, width: '100%', borderRadius: 'var(--radius-sm)',
                                 border: '1px solid var(--line)', background: '#0d1015' }} />;
        }

export { AeaExposureMap };
